<template>
  <div class="signup-form vuejs-signup-form">
    <form-component class="signup-form__form" :modifiers="['full-width-xs']" action="/nc/signup" method="post">
      <input
        v-if="lastPageBeforeLoginValue"
        type="hidden"
        name="last_page_before_login"
        :value="lastPageBeforeLoginValue"
      />

      <input type="hidden" name="authenticity_token" :value="authenticityToken" />

      <input name="ph_distinct_id" :value="ph_distinct_id" type="hidden" />

      <input type="hidden" name="signup[facebook_access_token]" class="js-signup-form-fb-access-token" value="" />

      <input type="hidden" name="signup[google_id_token]" class="js-google-id-token" value="" />

      <input type="hidden" name="signup[form_id]" :value="formId" />

      <input v-if="preSignupDataJson" type="hidden" name="signup[pre_signup_data]" :value="preSignupDataJson" />

      <input
        v-for="field in hiddenFields"
        :key="field.name + '-' + field.value"
        type="hidden"
        :name="field.name"
        :value="field.value"
      />

      <input-text
        class="signup-form__input"
        name="signup[email]"
        :required="true"
        :placeholder="emailCopy"
        v-model="email"
        :readonly="!!initialEmail"
      />

      <input-text
        class="signup-form__input"
        name="signup[password]"
        :required="true"
        :type="'password'"
        :minlength="8"
        :placeholder="$t('password')"
        v-model="password"
      />

      <button-vue
        class="signup-form__cta"
        ref="createAccountButton"
        :re-clickable="false"
        :modifiers="['full']"
        :copy="$t('cta')"
        :disabled="disabled"
      />

      <div v-if="!simplifiedForm">
        <div class="signup-form__separator">
          <span>{{ $t("or") }}</span>
        </div>

        <button-vue
          class="mb-4 signup-form__facebook-button"
          :modifiers="['facebook', 'full', 'icon-32']"
          :disabled="disabled"
          data-action="facebook-login"
          data-action-url=".js-signup-form-fb-access-token"
        >
          <img :src="$options.fbIcon" loading="lazy" alt="Facebook Icon" />
          <span v-html="$t('facebookCta')" />
        </button-vue>
      </div>

      <button-vue
        v-if="showGoogleButton"
        type="button"
        class="mb-4 signup-form__google-button js-google-signin"
        :modifiers="['google', 'full', 'icon-32']"
        :disabled="disabled"
      >
        <img :src="$options.googleIcon" loading="lazy" alt="Google Icon" />
        <span v-html="$t('googleCta')" />
      </button-vue>

      <apple-login-button
        v-if="showAppleButton"
        class="mb-4"
        :disabled="disabled"
        :last-page-before-login-value="lastPageBeforeLoginValue"
        :pre-signup-data-json="preSignupDataJson"
      />

      <button-link class="block mt-4" variant="product-interaction" :href="authB2BLink">
        {{ $t("ssoCta") }}
      </button-link>

      <div :class="`mt-4 signup-form__legal ${legalTextAlignment}`" v-html="$t('legal')" />
    </form-component>
  </div>
</template>

<script>
import FormComponent from "@/shared/form/form"
import InputText from "@/shared/form/input-text"
import ButtonVue from "@/shared/button"
import ButtonLink from "@/component-library/button-link"
import AppleLoginButton from "@/component-library/apple-login-button/index"
import { mapGetters } from "vuex"
import events from "@/bl-events"
import fbIcon from "./images/facebook-icon.svg"
import googleIcon from "./images/google-icon.svg"
import posthog from "posthog-js"

export default {
  name: "SignupForm",
  components: {
    FormComponent,
    InputText,
    ButtonVue,
    ButtonLink,
    AppleLoginButton
  },
  props: {
    formId: {
      type: String,
      default: "body-20210521-generic_signup_form"
    },
    hiddenFields: {
      type: Array,
      default: () => []
    },
    lastPageBeforeLogin: {
      type: String,
      default: ""
    },
    legalTextAlignment: {
      type: String,
      default: "text-center"
    },
    simplifiedForm: {
      type: Boolean,
      default: false
    },
    b2bForm: {
      type: Boolean,
      default: false
    },
    initialEmail: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      // eslint-disable-next-line
      ph_distinct_id: posthog.get_distinct_id(),
      email: this.initialEmail,
      password: null
    }
  },
  fbIcon,
  googleIcon,
  computed: {
    showAppleButton() {
      return !this.simplifiedForm
    },
    showGoogleButton() {
      return !this.b2bForm
    },
    disabled() {
      return !this.authenticityToken
    },
    lastPageBeforeLoginValue() {
      if (this.lastPageBeforeLogin) {
        return this.lastPageBeforeLogin
      }

      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get("last_page_before_login")
    },
    emailCopy() {
      if (this.b2bForm) {
        return this.$t("workEmail")
      } else {
        return this.$t("email")
      }
    },
    authB2BLink() {
      let url = "/auth/b2b"

      if (this.lastPageBeforeLoginValue) {
        url = `${url}?last_page_before_login=${this.lastPageBeforeLoginValue}`
      }

      return url
    },
    ...mapGetters(["authenticityToken"]),
    ...mapGetters("onboarding", ["preSignupDataJson"])
  },
  watch: {
    email() {
      this.reEnableCreateAccountButton()
    },
    password() {
      this.reEnableCreateAccountButton()
    }
  },
  mounted() {
    events.emit("facebook:init")
    events.emit("google:init")
  },
  methods: {
    reEnableCreateAccountButton() {
      // This is used to make the button enabled again after the user made changes to the form input.
      this.$refs.createAccountButton.resetClickCounter()
    }
  },
  i18n: {
    messages: {
      en: {
        email: "Email address",
        workEmail: "Work email address",
        password: "Create password",
        cta: "Create account",
        or: "or",
        facebookCta: "Sign up with Facebook",
        googleCta: "Sign up with Google",
        ssoCta: "Sign up with SSO",
        legal:
          "Start your learning journey! By submitting, you'll get exciting email updates about our products. Unsubscribe anytime via the link or <a href='mailto:support@blinkist.com' target='__blank'>support@blinkist.com</a>. Discover more about how we protect your data in our <a target='_blank' class='text-mid-grey underline' href='/privacy'>Privacy Policy</a>. Our <a target='_blank' href='/tos' class='text-mid-grey underline'>Terms</a> apply."
      },
      de: {
        email: "E-Mail Adresse",
        password: "Passwort erstellen",
        cta: "Konto erstellen",
        or: "oder",
        facebookCta: "Mit Facebook registrieren",
        googleCta: "Mit Google anmelden",
        ssoCta: "Mit SSO registrieren",
        legal:
          "Beginne deine Lernreise! Melde dich an und erhalte spannende E-Mail-Updates über unsere Produkte. Du kannst dich jederzeit wieder abmelden. Klicke dazu einfach auf den Link oder schreibe eine E-Mail an <a href='mailto:support@blinkist.com' target='__blank'>support@blinkist.com</a>. Wie wir deine Daten schützen, erfährst du in unserer <a target='_blank' class='text-mid-grey underline' href='/privacy'>Datenschutzrichtlinie</a>. Es gelten unsere <a target='_blank' href='/tos' class='text-mid-grey underline'>Allgemeinen Geschäftsbedingungen</a>."
      },
      es: {
        email: "Dirección de correo electrónico",
        workEmail: "Dirección de correo electrónico de trabajo",
        password: "Crear contraseña",
        cta: "Crear cuenta",
        or: "o",
        facebookCta: "Registrarse con Facebook",
        googleCta: "Registrarse con Google",
        ssoCta: "Registrarse con SSO",
        legal:
          "¡Inicia tu aprendizaje! Al inscribirte, recibirás emocionantes actualizaciones por correo electrónico sobre nuestros productos. Puedes darte de baja en cualquier momento a través del enlace o escribiendo a <a href='mailto:support@blinkist.com' target='__blank'>support@blinkist.com</a>. Descubre más sobre cómo protegemos tus datos en nuestra <a target='_blank' class='text-mid-grey underline' href='/privacy'>Política de privacidad</a>. Nuestros <a target='_blank' href='/tos' class='text-mid-grey underline'>Términos y Condiciones</a> aplican."
      },
      pt: {
        email: "Endereço de e-mail",
        workEmail: "Endereço de e-mail profissional",
        password: "Criar senha",
        cta: "Criar conta",
        or: "ou",
        facebookCta: "Registrar-se com Facebook",
        googleCta: "Registrar-se com Google",
        ssoCta: "Cadastrar com SSO",
        legal:
          "Comece seu aprendizado! Ao se inscrever, você receberá atualizações empolgantes por e-mail sobre nossos produtos. Você pode cancelar a inscrição a qualquer momento através do link ou escrevendo para <a href='mailto:support@blinkist.com' target='__blank'>support@blinkist.com</a>. Saiba mais sobre como protegemos seus dados em nossa <a target='_blank' class='text-mid-grey underline' href='/privacy'>Política de Privacidade</a>. Nossos <a target='_blank' href='/tos' class='text-mid-grey underline'>Termos e Condições</a> se aplicam."
      }
    }
  }
}
</script>

<style lang="scss">
.signup-form {
  @include font-family(sans-serif);

  &__form {
    max-width: 100%;

    @include media(min-width, m) {
      max-width: inherit;
    }
  }

  &__email-input-label {
    @include font-style(h5, $color: $mid-grey);
  }

  &__input {
    margin-bottom: $spacing-16;
  }

  &__separator {
    width: 100%;
    margin: $spacing-32 0 $spacing-32;

    color: $mid-grey;
    font-size: spacing(14px);
    line-height: spacing(1px);
    text-align: center;

    border-bottom: spacing(1px) solid $light-grey;

    span {
      @include font-style(caption, $weight: medium, $color: $mid-grey, $line-height: spacing(1px));

      padding: 0 $spacing-16;

      background-color: $white;
    }
  }

  &__facebook-button {
    position: relative;

    img {
      position: absolute;
      top: 50%;
      left: $spacing-16;

      height: $spacing-24;

      transform: translateY(-50%);
    }

    span {
      margin-left: $spacing-16;

      @include media(min-width, s) {
        margin-left: 0;
      }
    }
  }

  &__google-button {
    position: relative;

    background-color: #4285f4;

    &:hover {
      background-color: #3367d6;
    }

    img {
      position: absolute;
      top: 50%;
      left: rem(1px);

      height: rem(40px);

      transform: translateY(-50%);
    }

    span {
      margin-left: $spacing-16;

      @include media(min-width, s) {
        margin-left: 0;
      }
    }
  }

  &__legal {
    @include font-style(legal);

    a {
      @include font-style(legal);

      text-decoration: underline;
    }
  }
}
</style>
